<template lang="pug">
  .sosSelectWrapper
    label.sosSelectTitle
      span(v-if="labelDescription" v-html="`${label} <span class='vLabelDescription'>${labelDescription}</span>`")
      span(v-else v-html="`${label}`")

    .sosTimeSelectField
      .sosSelect(
        ref="sosHourSelect"
        @mousedown="hourMouseDownEvent => openHour(hourMouseDownEvent, 'auto')"
        :class="{ 'sosSelect--showing': showingHour, 'sosSelect--disabled': disabled }"
        tabindex="0"
      )
        .sosSelectSelected {{ selectedHourText }}
        .sosSelectOptions(:class="{ 'sosSelectOptions--show': showingHour }" @mousedown.stop)
          .sosSelectOption(
            v-if="hours.length > 0"
            v-for="(itemHour, itemHourIndex) in hours"
            :key="`sos-select-item-${itemHour.key || itemHourIndex}`"
            @click="onHourSelect(itemHourIndex)"
            :class="{ 'sosSelectOption--selected': selectedHourValue === itemHour.value, 'sosSelectOption--first': itemHourIndex === 0 }"
          )
            | {{ itemHour.text || '&nbsp;' }}

      .sosSelectDivider {{ ':' }}

      .sosMinuteInputWrapper
        input.sosMinuteInput(
          type="text"
          v-model="selectedMinuteValue"
          maxlength="2"
          placeholder="Min"
          :disabled="disabled || isMinuteDisabled"
          @focus="selectText"
          @input="handleMinuteInput"
          @blur="handleMinuteBlur"
        )
</template>

<script>
  export default {
    name: 'SosHybridTimeSelect',
    props: {
      label: { type: String, required: true },
      hours: { type: Array, required: true },
      disabled: { type: Boolean, default: () => false },
      labelDescription: { type: String, default: () => '' },
      default: { type: Object, default: () => ({ hour: '', minute: '00' }) },
      lockMinutesOn24h: { type: Boolean, default: () => false },
    },
    data () {
      return {
        showingHour: false,
        selectedHourValue: '',
        selectedHourText: 'Horas',
        selectedMinuteValue: '00',
      }
    },
    computed: {

      isMinuteDisabled() {
        return this.lockMinutesOn24h && this.selectedHourValue === '24';
      }
    },
    watch: {
      default: {
        immediate: true,
        handler (defaultNew) {
          if (typeof defaultNew === 'object') {
            this.refreshProps();
          }
        },
      },
      disabled(newValue) {
        if (newValue) {
          this.showingHour = false;
        }
      },

      selectedHourValue(newValue) {
        if (this.lockMinutesOn24h && newValue === '24') {
          this.selectedMinuteValue = '00';
        }
      }
    },
    mounted () {
      this.refreshProps();
      document.addEventListener('mousedown', this.close);
    },
    beforeDestroy () {
      document.removeEventListener('mousedown', this.close);
    },
    methods: {
      openHour (event, status) {
        if (this.disabled) return;
        if (event.which === 3) return;
        this.showingHour = status === 'auto' ? !this.showingHour : status;
      },
      close (event) {
        if (this.$refs.sosHourSelect && !this.$refs.sosHourSelect.contains(event.target)) {
          this.showingHour = false;
        }
      },
      onHourSelect (itemIndex) {
        this.showingHour = false;
        const hourItem = this.hours[itemIndex] || null;
        if (hourItem) {
          const changed = hourItem.value !== this.selectedHourValue;
          this.selectedHourValue = hourItem.value;
          this.selectedHourText = hourItem.text;

          if (this.lockMinutesOn24h && hourItem.value === '24') {
            this.selectedMinuteValue = '00';
          }

          this.emitChange();


        }
      },

      refreshProps () {
        const defaultHour = this.default?.hour || '';
        const defaultMinute = this.default?.minute || '00';

        const getHour = this.hours.find(h => h.value === defaultHour);
        this.selectedHourValue = getHour ? getHour.value : '';
        this.selectedHourText = getHour ? getHour.text : 'Horas';

        this.selectedMinuteValue = defaultMinute;


      },

      selectText(event) {
        event.target.select();
      },

      handleMinuteInput() {
        if (this.isMinuteDisabled) {
          this.selectedMinuteValue = '00';
          return;
        }

        const oldValue = this.selectedMinuteValue;
        let digits = (oldValue || '').replace(/\D/g, '');

        if ((oldValue === '00' || oldValue.startsWith('0')) && digits.length > 1) {
          digits = digits.substring(1);
        }

        const finalValue = digits.slice(0, 2);
        if (this.selectedMinuteValue !== finalValue) {
          this.selectedMinuteValue = finalValue;
        }

        this.emitChange();
      },

      handleMinuteBlur() {
        if (this.isMinuteDisabled) {
          this.selectedMinuteValue = '00';
          this.emitChange();
          return;
        }

        let value = this.selectedMinuteValue;
        if (!value) value = '00';
        if (value.length === 1) value = value.padStart(2, '0');

        if (parseInt(value) > 59) {
          value = '59';
        }

        this.selectedMinuteValue = value;
        this.emitChange();
      },

      emitChange() {
        const payload = {
          hour: this.selectedHourValue,
          minute: this.selectedMinuteValue
        };

        this.$emit('sosSelectChanged', payload);

        this.$emit('input', payload);
        this.$emit('change', payload);

      },
    },
  }
</script>

<style lang="scss" scoped>
  .sosSelectWrapper {
    width: 100%;
    height: auto;
  }

  .sosSelectTitle {
    display: block;
    margin-bottom: 8px;
    font-weight: bold;
    line-height: 1.2;
  }

  .sosTimeSelectField {
    display: flex;
    align-items: center;
  }

  .sosSelect {
    display: flex;
    align-items: center;
    justify-content: center;
    position: relative;
    border: 1px solid #ccc;
    padding: 8px 12px;
    cursor: pointer;
    background-color: white;
    min-width: 100px;
    border-radius: 4px;
    user-select: none;
    flex: 1;

    &:focus {
      outline: 2px solid #4A90E2;
      outline-offset: 2px;
    }
  }

  .sosSelect--showing {
    border-color: #4A90E2;
  }

  .sosSelect--disabled {
    background-color: #f5f5f5;
    cursor: not-allowed;
    opacity: 0.7;
  }

  .sosSelectOptions {
    display: none;
    position: absolute;
    top: 100%;
    left: 0;
    right: 0;
    background-color: white;
    border: 1px solid #ccc;
    border-top: none;
    z-index: 10;
    max-height: 200px;
    overflow-y: auto;
    margin-top: -1px;
    box-shadow: 0 4px 6px rgba(0,0,0,0.1);
  }

  .sosSelectOptions--show {
    display: block;
  }

  .sosSelectOption {
    padding: 8px 12px;
    cursor: pointer;
    transition: background-color 0.2s;

    &:hover {
      background-color: #f0f0f0;
    }
  }

  .sosSelectOption--selected {
    background-color: #e0e0e0;
    font-weight: bold;
  }

  .sosSelectDivider {
    font-size: 1.5rem;
    font-weight: bold;
    color: #555;
    margin: 0 8px;
  }

  .sosMinuteInputWrapper {
    min-width: 100px;
    flex: 1;
  }

  .sosMinuteInput {
    width: 100%;
    border: 1px solid #ccc;
    padding: 8px 12px;
    border-radius: 4px;
    font-size: inherit;
    font-family: inherit;
    text-align: center;
    box-sizing: border-box;

    &:focus {
      outline: 2px solid #4A90E2;
      outline-offset: 2px;
      border-color: #4A90E2;
    }

    &:disabled {
      background-color: #f5f5f5;
      cursor: not-allowed;
      opacity: 0.7;
    }
  }
</style>
